import { GetUsersParams, UserStatus, UserType } from "@bvhi/backend-api";
import { Button, BVHISelect, Card, CardContent, CardHeader, Collapse, Grid, TextField } from "@bvhi/ui-base";
import { ClearIcon } from "@bvhi/ui-icons";
import isEqual from "lodash/isEqual";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIExpandComponent } from "../../../../../../components/bvhi-expand-component";
import { usePermissions } from "../../../../../../hooks/use-permissions";
import { defaultUsersFilter } from "./default-users-filter";

interface Props {
  filter: GetUsersParams;
  onChange: ({ key, value }: { key: string; value: string | string[] }) => void;
  onReset: () => void;
}

export const UsersFilterCard = ({ filter, onChange, onReset }: Props) => {
  const { t } = useTranslation(["users", "userstatus", "usertype"]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { hasPermissionTo } = usePermissions();
  const canFilterTypes = hasPermissionTo(["user:set-type"]);

  const isResetDisabled = useMemo(() => {
    return isEqual(filter, defaultUsersFilter);
  }, [filter]);

  return (
    <Card>
      <CardHeader
        restrictContentWidth={false}
        title={t("users:users.card.filter.title")}
        action={
          <Button color="secondary" disabled={isResetDisabled} endIcon={<ClearIcon />} onClick={onReset} variant="text">
            {t("users:users.card.filter.button.reset")}
          </Button>
        }
      />
      <CardContent>
        <Grid container pb={2} spacing={2}>
          <Grid xs={12}>
            <TextField
              fullWidth
              label={t("users:users.filter.searchtext.label")}
              placeholder={t("users.filter.searchtext.placeholder")}
              value={filter.searchString ?? ""}
              onChange={(e) => {
                onChange({ key: "searchString", value: e.target.value });
              }}
            />
          </Grid>
        </Grid>
        <BVHIExpandComponent expanded={expanded} setExpanded={setExpanded} />
        <Collapse in={expanded} timeout="auto">
          <Grid container spacing={2}>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("users:datagrid.firstName")}
                placeholder={t("users.filter.firstName.placeholder")}
                value={filter.firstName ?? ""}
                onChange={(e) => {
                  onChange({ key: "firstName", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("users:datagrid.lastName")}
                placeholder={t("users.filter.lastName.placeholder")}
                value={filter.lastName ?? ""}
                onChange={(e) => {
                  onChange({ key: "lastName", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("users:datagrid.username")}
                placeholder={t("users:datagrid.username")}
                value={filter.username ?? ""}
                onChange={(e) => {
                  onChange({ key: "username", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <TextField
                fullWidth
                label={t("users:datagrid.email")}
                placeholder={t("users.filter.email.placeholder")}
                value={filter.email ?? ""}
                onChange={(e) => {
                  onChange({ key: "email", value: e.target.value });
                }}
              />
            </Grid>
            <Grid xs={6}>
              <BVHISelect
                displayEmpty
                fullWidth
                label={t("users:datagrid.status")}
                multiple
                showReset
                value={filter.status ?? []}
                handleChange={(value) => {
                  onChange({ key: "status", value: value });
                }}
                handleReset={() => {
                  onChange({ key: "status", value: [] });
                }}
                items={[
                  { key: UserStatus.ACTIVE, value: UserStatus.ACTIVE, label: t("userstatus:ACTIVE") },
                  { key: UserStatus.INACTIVE, value: UserStatus.INACTIVE, label: t("userstatus:INACTIVE") },
                ]}
              />
            </Grid>
            {canFilterTypes && (
              <Grid xs={6}>
                <BVHISelect
                  displayEmpty
                  fullWidth
                  label={t("users:datagrid.type")}
                  showReset
                  value={filter.type ?? ""}
                  handleChange={(value) => {
                    onChange({ key: "type", value: value });
                  }}
                  handleReset={() => {
                    onChange({ key: "type", value: "" });
                  }}
                  items={[
                    { key: "", value: "", label: t("usertype:NONE") },
                    { key: UserType.AUDIOLOGIST, value: UserType.AUDIOLOGIST, label: t("usertype:AUDIOLOGIST") },
                    { key: UserType.ADMIN, value: UserType.ADMIN, label: t("usertype:ADMIN") },
                    { key: UserType.ERP_SUPPLIER, value: UserType.ERP_SUPPLIER, label: t("usertype:ERP_SUPPLIER") },
                    { key: UserType.SUPPLIER, value: UserType.SUPPLIER, label: t("usertype:SUPPLIER") },
                  ]}
                />
              </Grid>
            )}
          </Grid>
        </Collapse>
      </CardContent>
    </Card>
  );
};
